// API Error utilities
// Provides a consistent error shape and centralized error handling for API calls.

import { toast } from 'react-toastify';
import { secureError } from './secureLogger';

export class ApiError extends Error {
  constructor(message, { status, code, details } = {}) {
    super(message || 'API Error');
    this.name = 'ApiError';
    this.status = status ?? null;
    this.code = code ?? null;
    this.details = details ?? null;
  }
}

/**
 * Create an ApiError from a fetch Response
 * Ensures we always have a meaningful message and status code.
 */
export async function createApiErrorFromResponse(response) {
  let payload = {};

  try {
    // Attempt to parse JSON; if it fails, fall back to empty object
    payload = await response.json();
  } catch (_) {
    payload = {};
  }

  const message =
    payload.error ||
    payload.message ||
    `HTTP ${response.status}: ${response.statusText || 'Request failed'}`;

  const code = payload.code || payload.errorCode || null;

  return new ApiError(message, {
    status: response.status,
    code,
    details: payload,
  });
}

/**
 * Standardized API error handler
 * - Ignores AbortError (canceled requests)
 * - Logs via secureError
 * - Shows a toast with a user-friendly message
 */
export function handleApiError(
  error,
  { defaultMessage = 'Something went wrong', context = 'API' } = {}
) {
  if (!error) return;

  // Ignore aborted requests
  if (error.name === 'AbortError') {
    return;
  }

  let message = defaultMessage;

  if (error instanceof ApiError) {
    message = error.message || defaultMessage;
  } else if (error && error.message) {
    message = error.message;
  }

  // Secure logging (redacts PII, sends to Sentry in production)
  secureError(`[${context}] ${message}`, error);

  // User-facing toast
  try {
    toast.error(message);
  } catch (_) {
    // In case toast is not initialized, fail silently
  }
}